import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { InView } from "react-intersection-observer";
import { IndianRupee, Loader2, X } from "lucide-react";
import {
  Button,
  Checkbox,
  CurrencyInput,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  SearchInput,
  Tabs,
  TabsList,
  TabsTrigger,
  VisuallyHidden,
  cn,
} from "web-design-system";
import { handleError } from "web-core/utils";
import {
  useAddPortfolioReviewLine,
  useFetchSchemesByIsin,
  useInfiniteFunds,
} from "creso-core/hooks";
import { Fund, PortfolioReview } from "creso-core/types";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import { CategoryPicker } from "./category-picker";
import { EditColumnsMenu } from "./edit-columns-menu";
import { FavouritesEmptyState } from "./favourites-empty-state";
import { FundCell } from "./fund-badges";
import { PICK_COLUMNS, PickColumnKey } from "./holding-columns";

type PickedFund = { fund: Fund; amount: string };

// What exits free up, less what new additions already take. Lines without
// an explicit amount move their whole current value.
export function releasedAmount(review: PortfolioReview) {
  const sum = (bucket: string) =>
    review.lines
      .filter((line) => line.bucket === bucket)
      .reduce(
        (total, line) => total + Number(line.amount ?? line.current_value),
        0
      );
  return Math.ceil(Math.max(sum("EXIT") - sum("NEW_ADDITION"), 0));
}

export function PickFundsDialog({
  review,
  open,
  onOpenChange,
}: {
  review: PortfolioReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-6xl flex-col gap-0 overflow-hidden p-0">
        <VisuallyHidden>
          <DialogTitle>Pick fund</DialogTitle>
          <DialogDescription>
            Pick new funds to add to the portfolio and allocate an amount to
            each.
          </DialogDescription>
        </VisuallyHidden>
        {open ? (
          <FundPicker review={review} onDone={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FundPicker({
  review,
  onDone,
}: {
  review: PortfolioReview;
  onDone: () => void;
}) {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "favourites">("all");
  const showFavourites = tab === "favourites";
  const [debouncedSearch] = useDebounceValue(search, 400);
  const [categories, setCategories] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<PickColumnKey[]>(
    PICK_COLUMNS.filter((c) => c.group === "visible").map((c) => c.key)
  );
  const [picked, setPicked] = useState<PickedFund[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: addLine } = useAddPortfolioReviewLine();
  const fetchSchemesByIsin = useFetchSchemesByIsin("all");
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteFunds(
      {
        pageNo: 1,
        pageSize: 20,
        search: debouncedSearch,
        // Favourites are listed whole, whatever the category.
        filters: showFavourites
          ? { favourites: "true" }
          : { fund_level_category_code_in: categories },
        // Ranks only compare funds within one category.
        sort:
          !showFavourites && categories.length === 1
            ? [{ id: "rankings__category_rank", desc: false }]
            : undefined,
      },
      { maxPages: 0 }
    );
  const funds = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );

  const columns = PICK_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  // A search with no matches is not the same as having no favourites.
  const noFavourites =
    showFavourites && !debouncedSearch && !isLoading && !funds.length;
  const inReview = new Set(
    review.lines.map((line) => line.target_fund_id).filter(Boolean)
  );
  const released = releasedAmount(review);
  const allocated = picked.reduce((total, f) => total + Number(f.amount), 0);
  const canSave =
    picked.length > 0 &&
    picked.every((f) => Number(f.amount) > 0) &&
    allocated <= released;

  const toggle = (fund: Fund) =>
    setPicked((current) =>
      current.some((f) => f.fund.fund_id === fund.fund_id)
        ? current.filter((f) => f.fund.fund_id !== fund.fund_id)
        : [...current, { fund, amount: "" }]
    );

  const setAmount = (fund: Fund, amount = "") =>
    setPicked((current) =>
      current.map((f) =>
        f.fund.fund_id === fund.fund_id ? { ...f, amount } : f
      )
    );

  const save = async () => {
    setIsSaving(true);
    try {
      // Review lines point at transactable schemes, so map the funds by ISIN.
      const schemes = await fetchSchemesByIsin(picked.map((f) => f.fund.isin));
      const schemeIdOf = (fund: Fund) => {
        const scheme = schemes.find((s) => s.scheme_isin === fund.isin);
        if (!scheme) throw new Error(`${fund.fund_name} cannot be purchased`);
        return scheme.fund_scheme_id ?? scheme.id;
      };
      // One at a time: each add recomputes the review's insights.
      await picked.reduce<Promise<unknown>>(
        (previous, { fund, amount }) =>
          previous.then(() =>
            addLine({
              reviewId: review.id,
              target_fund_scheme: schemeIdOf(fund),
              amount: Number(amount),
            })
          ),
        Promise.resolve()
      );
      onDone();
    } catch (error) {
      handleError(error, "Could not add the funds");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-1 min-h-0 flex-col gap-4 p-6">
          <span className="text-lg font-semibold mb-2">Pick fund</span>
          <Tabs
            value={tab}
            onValueChange={(value) => setTab(value as typeof tab)}
          >
            <TabsList>
              <TabsTrigger value="all">All funds</TabsTrigger>
              <TabsTrigger value="favourites">Favourites</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-row items-center gap-4">
            <div className="w-85">
              <SearchInput
                placeholder="Search for funds"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {showFavourites ? null : (
              <CategoryPicker value={categories} onChange={setCategories} />
            )}
            <div className="ml-auto">
              <EditColumnsMenu
                columns={PICK_COLUMNS}
                visible={visibleColumns}
                onChange={setVisibleColumns}
              />
            </div>
          </div>

          {noFavourites ? (
            <FavouritesEmptyState />
          ) : (
            <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-card">
                  <tr>
                    <th className="w-12 p-4" />
                    <th className="p-4 text-left font-semibold">Fund Name</th>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap p-4 text-right font-semibold"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {funds.map((fund) => (
                    <FundRow
                      key={fund.fund_id}
                      fund={fund}
                      columns={columns.map((c) => c.key)}
                      added={inReview.has(fund.fund_id)}
                      checked={picked.some(
                        (f) => f.fund.fund_id === fund.fund_id
                      )}
                      onToggle={() => toggle(fund)}
                    />
                  ))}
                </tbody>
              </table>
              {isLoading ? (
                <div className="flex justify-center p-6">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : !funds.length ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  {showFavourites
                    ? "No favourite funds found"
                    : "No funds found"}
                </p>
              ) : null}
              {hasNextPage ? (
                <InView
                  as="div"
                  onChange={(inView) => {
                    if (inView && !isFetchingNextPage) fetchNextPage();
                  }}
                  className="flex justify-center p-4"
                >
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </InView>
              ) : null}
            </div>
          )}
        </div>

        <aside className="flex min-h-0 flex-col gap-4 bg-card p-4 pt-6">
          <span className="text-lg font-semibold">Re-allocate amount</span>
          <div className="flex min-h-0 py-2 px-1 flex-1 flex-col overflow-auto">
            {picked.length ? (
              picked.map(({ fund, amount }) => (
                <div
                  key={fund.fund_id}
                  className="flex flex-col gap-4 border-b border-border last:border-transparent py-6 first:pt-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <FundCell
                      name={fund.fund_name}
                      category={fund.fund_level_category_name}
                      amcCode={fund.provider_company_id}
                      fundId={fund.fund_id}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${fund.fund_name}`}
                      onClick={() => toggle(fund)}
                    >
                      <X />
                    </Button>
                  </div>
                  <CurrencyInput
                    aria-label={`Amount for ${fund.fund_name}`}
                    placeholder="Enter amount"
                    value={amount}
                    onValueChange={(value) => setAmount(fund, value)}
                  />
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick funds from the list to re-allocate the amount freed by
                exits.
              </p>
            )}
          </div>
          <AvailableAmount remaining={released - allocated} />
        </aside>
      </div>
      <DialogFooter className="border-t border-border p-4">
        <Button disabled={!canSave || isSaving} onClick={save}>
          {isSaving ? <Loader2 className="animate-spin" /> : null}
          Recommend
        </Button>
      </DialogFooter>
    </>
  );
}

function FundRow({
  fund,
  columns,
  added,
  checked,
  onToggle,
}: {
  fund: Fund;
  columns: PickColumnKey[];
  added: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const cells: Record<PickColumnKey, React.ReactNode> = {
    rank: fund.ranking?.category_rank ?? "-",
    aum: formatAsCrore(fund.assets_under_management, 0),
    return_1y: formatAsPercentage(fund.performance?.return_1_year, "-"),
    return_3y: formatAsPercentage(fund.performance?.return_3_year, "-"),
    return_5y: formatAsPercentage(fund.performance?.return_5_year, "-"),
  };

  return (
    <tr
      onClick={() => !added && onToggle()}
      className={cn(
        "border-t border-border hover:bg-muted/80",
        added && "opacity-50"
      )}
    >
      <td className="px-4 py-3">
        <Checkbox
          aria-label={fund.fund_name}
          checked={checked || added}
          disabled={added}
          onClick={(e) => e.stopPropagation()}
          onCheckedChange={onToggle}
        />
      </td>
      <td className="px-4 py-3">
        <FundCell
          name={fund.fund_name}
          category={added ? "Already in review" : fund.fund_level_category_name}
          amcCode={fund.provider_company_id}
        />
      </td>
      {columns.map((key) => (
        <td key={key} className="whitespace-nowrap px-4 py-3 text-right">
          {cells[key]}
        </td>
      ))}
    </tr>
  );
}

function AvailableAmount({ remaining }: { remaining: number }) {
  const over = remaining < 0;
  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center gap-4 rounded-lg bg-background p-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary">
          <IndianRupee className="size-5" />
        </span>
        <div className="flex flex-col">
          <span
            className={cn("font-semibold text-sm", over && "text-destructive")}
          >
            {convertToCurrency(Math.abs(remaining))}
          </span>
          <span className="text-xs text-muted-foreground">
            {over
              ? "more than available to re-allocate"
              : "available to re-allocate"}
          </span>
        </div>
      </div>
    </div>
  );
}
